import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import HorizontalSpace from "../HorizontalSpace";
import "../../assets/css/createlessons.css";
import VerticalSpace from "../VerticalSpace";
import RadioButton from "../Common/RadioButton";
import deleteIcon from "../../assets/deleteIcon.svg";
import editIcon from "../../assets/editIcon.svg";
import copyIcon from "../../assets/copyIcon.svg";
import clipboardIcon from "../../assets/clipboard.svg";
import cutIcon from "../../assets/cutIcon.svg";
import pasteIcon from "../../assets/pasteIcon.svg"
import { useParams } from "react-router-dom";
import Label from "../Common/Label";
import { useNavigate, useLocation } from "react-router-dom";
import {
  baseURL,
  deleteRequest,
  getRequest,
  multiPostRequest,
  postRequest,
} from "./../../api/apiClient";
import "react-toastify/dist/ReactToastify.css";
import Toast from "../Common/Toast";
import RowContainer from "../Common/RowContainer";
import ColumnContainer from "../Common/ColumnContainer";
import DeleteConfirmation from "../Common/DeleteConfirmation";
import Loader from "../../utils/Loading";
import gsap from "gsap";
import "../../stylesheets/common.css";
import DeleteLoader from "../../utils/deleteLoader";
import NestedMenu from "../ParticlesDropdown/NestedMenu";
import { DropdownValue, Pair } from "../../types";
import { ensureParticleValue1AtEnd, ensureParticleValueAtStart, getParticleReadOnlyStatus, particlePairValues } from "../../utils/particlePairUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  copyToClipboard,
  cutToClipboard,
  clearClipboard,
  selectClipboard,
} from "../../redux/clipboardSlice";

interface Theme {
  id: number;
  name: string;
  cards: number;
  order?: number;
  color?: string;
}

const CreateLessons = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const clipboard = useSelector(selectClipboard);
  const [addThemes, setNewThemes] = useState(false);
  const [isPasting, setIsPasting] = useState(false);
  const [selectedThemes, setSelectedThemes] = useState<number[]>([]);
  const [showCoverForm, setShowCoverForm] = useState(true);
  const [selectedButton, setSelectedButton] = useState("Cover");
  const [themeName, setThemeName] = useState("");
  const [title, setTitle] = useState("");
  const [titleColor, setTitleColor] = useState("#000000");
  const [subTitleColor, setSubTitleColor] = useState("#000000");
  const [subTitleBgColor, setSubTitleBgColor] = useState("#FFFFFF");
  const [themes, setThemes] = useState<Theme[]>([]);
  const [editThemes, setEditThemes] = useState<Theme | null>(null);
  const [level, setLevel] = useState("A1");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const startingLessonInputRef = useRef<HTMLInputElement>(null);
  const myLessonsInputRef = useRef<HTMLInputElement>(null);
  const [coverImage, setCoverImage] = useState("");
  const [themeError, setThemeError] = useState("");
  const [deleteDialogMessage, setDeleteDialogMessage] = useState("");
  const [onConfirmDelete, setOnConfirmDelete] = useState<() => void>(() => { });
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [pairToDelete, setPairToDelete] = useState<{
    index: number;
    id?: number;
  } | null>(null);
  const [validationError, setValidationError] = useState<{
    pairs: {
      [key: number]: {
        dropdownValue?: string;
        inputValue?: string;
      };
    };
  }>({
    pairs: {},
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [themeToDelete, setThemeToDelete] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const listPage: number = (location.state as any)?.listPage ?? 0;
  const listLimit: number = (location.state as any)?.listLimit ?? 5;
  const listSearch: string = (location.state as any)?.listSearch ?? "";
  const listFilter: string | null = (location.state as any)?.listFilter ?? null;
  const listLevel: string | null = (location.state as any)?.listLevel ?? null;
  const [errors, setErrors] = useState({
    title: "",
    // coverImage: "",
  });
  const [lessonId, setLessonId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coverImageLoading, setCoverImageLoading] = useState(false);

  const fetchParticleById = async (id: number) => {
    try {
      const response = await getRequest(
        `${baseURL}/particle/get-particleById/${id}`
      );
      return response?.data.data;
    } catch (error) {
      console.error("Error fetching particle by ID:", error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchLessonData = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const response = await getRequest(
          `${baseURL}/lesson/get-lesson-theme/${id}`
        );

        const themeData = response?.data.data.themeData;
        const lessonData = response?.data.data.lesson;
        const particles = response?.data.data.particles;

        // Fetching cards count for each theme
        const cardsCountPromises = themeData.map(async (theme: any) => {
          const cardCountResponse = await postRequest(
            `${baseURL}/lesson/counts`,
            {
              modelName: "subthemes",
              field: "themeId",
              id: theme.id.toString(),
            }
          );
          if (cardCountResponse) {
            return { ...theme, cards: cardCountResponse.data.data.count };
          } else {
            return { ...theme, cards: 0 };
          }
        });

        const resolvedCardsCount = await Promise.all(cardsCountPromises);
        setLevel(lessonData.level);
        setTitle(lessonData.title);
        setTitleColor(lessonData.titleColor);
        setSubTitleColor(lessonData.subTitleColor);
        setSubTitleBgColor(lessonData.subTitleBgColor);
        setCoverImage(lessonData.coverImage);
        setThemes(resolvedCardsCount);
        setLessonId(lessonData.id);

        // Load saved Partículas pairs
        if (particles && particles.length > 0) {
          const fetchedPairs = await Promise.all(
            particles.map(async (item: any, index: number) => {
              const particleData = await fetchParticleById(item.type);
              return {
                id: item.id,
                dropdownValue: {
                  id: particleData.id,
                  name: particleData.name,
                  pId: particleData.pId,
                  particleType: particleData.particleType,
                  particleValue: particleData.particleValue,
                  particleType1: particleData.particleType1,
                  particleValue1: particleData.particleValue1,
                  children: particleData.children || [],
                },
                inputValue: item.value,
                order: item.order || index + 1,
                isReadOnly: getParticleReadOnlyStatus(particleData),
              };
            })
          );
          setPairs(fetchedPairs);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching themes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLessonData();
  }, [id]);

  const handleButtonClick = (buttonName: string) => {
    if (buttonName === "Cover") {
      setShowCoverForm(true);
    } else {
      setShowCoverForm(false);
    }
    setSelectedButton(buttonName);
  };

  const handleLevelChange = (level: string) => {
    setLevel(level);
  };

  const handleNewThemes = () => {
    const activeLessonId = lessonId || id;

    if (!activeLessonId) {
      Toast("Please create a lesson before adding themes.", "error");
      return;
    }
    setThemeName("");
    setThemeError("");
    setNewThemes(true);
    setEditThemes(null);
  };

  const handleCoverImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setCoverImageLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      const response = await multiPostRequest(
        `${baseURL}/file-upload`,
        formData
      );

      if (response?.data.success) {
        setCoverImage(response?.data?.data[0].uri);
      }
      setCoverImageLoading(false);
    }
  };

  const handleChooseFileClick = (inputType: string) => {
    const inputRef =
      inputType === "cover"
        ? fileInputRef
        : inputType === "startingLesson"
          ? startingLessonInputRef
          : myLessonsInputRef;
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleDeleteImage = async (imageType: string) => {
    let fileUri = "";

    switch (imageType) {
      case "cover":
        fileUri = coverImage;
        break;
      default:
        return;
    }

    try {
      const response = await postRequest(`${baseURL}/remove-file`, {
        fileUri,
      });

      if (response?.data.success) {
        switch (imageType) {
          case "cover":
            setCoverImage("");
            break;
          default:
            break;
        }
      } else {
        console.error("Failed to delete the image");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  //handle public click
  const handlePublishClick = async () => {
    const validationErrors = {
      title: !title ? "Title is required" : "",
      // coverImage: !coverImage ? "Cover image is required" : "",
    };

    setErrors(validationErrors);

    // If any validation error exists, stop the form submission
    if (Object.values(validationErrors).some((error) => error)) {
      return;
    }

    if (lessonId) {
      await handleUpdateLesson();
    } else {
      await handleCreateLesson();
    }
  };

  const handleCreateLesson = async () => {
    const payload = {
      title,
      level,
      titleColor,
      subTitleColor,
      subTitleBgColor,
      coverImage,
      themes: themes.map((theme) => ({
        id: theme.id,
        name: theme.name,
        cards: theme.cards,
        color: theme.color || "#FFFFFF",
      })),
      particles: pairs.map((pair) => ({
        id: pair.id || null,
        type: pair.dropdownValue?.id || null,
        value: pair.inputValue || "",
        order: pair.order || 0,
      })),
    };

    try {
      setIsLoading(true);

      const response = await postRequest(
        `${baseURL}/lesson/create-lesson`,
        payload
      );

      if (response?.data.success) {
        const createdLessonData = response.data.data.lesson;
        const sortedParticles = response.data.data.particles
        setLessonId(createdLessonData.id);
        setTitle(createdLessonData.title);
        setTitleColor(createdLessonData.titleColor);
        setSubTitleColor(createdLessonData.subTitleColor);
        setSubTitleBgColor(createdLessonData.subTitleBgColor);
        setCoverImage(createdLessonData.coverImage);

        if (sortedParticles && sortedParticles.length > 0) {
          const fetchedPairs = await Promise.all(
            sortedParticles.map(async (item: any, index: number) => {
              const particleData = await fetchParticleById(item.type);
              return {
                id: item.id,
                dropdownValue: {
                  id: particleData.id,
                  name: particleData.name,
                  pId: particleData.pId,
                  particleType: particleData.particleType,
                  particleValue: particleData.particleValue,
                  particleType1: particleData.particleType1,
                  particleValue1: particleData.particleValue1,
                  children: particleData.children || [],
                },
                inputValue: item.value,
                order: item.order || index + 1,
                isReadOnly: getParticleReadOnlyStatus(particleData),
              };
            })
          );
          setPairs(fetchedPairs);
        }
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleUpdateLesson = async () => {
    if (lessonId === null) {
      return;
    }

    const payload = {
      id: lessonId,
      title,
      level,
      titleColor,
      subTitleColor,
      subTitleBgColor,
      coverImage,
      themes: themes.map((theme) => ({
        id: theme.id,
        name: theme.name,
        cards: theme.cards,
        color: theme.color || "#FFFFFF",
      })),
      particles: pairs.map((pair) => ({
        id: pair.id || null,
        type: pair.dropdownValue?.id || null,
        value: pair.inputValue || "",
        order: pair.order || 0,
      })),
    };

    try {
      setIsLoading(true);
      const response = await postRequest(
        `${baseURL}/lesson/update-lessons`,
        payload
      );

      if (response?.data.success) {
        const updatedLessonData = response.data.data.lesson;
        const sortedParticles = response.data.data.particles

        setTitle(updatedLessonData.title);
        setTitleColor(updatedLessonData.titleColor);
        setSubTitleColor(updatedLessonData.subTitleColor);
        setSubTitleBgColor(updatedLessonData.subTitleBgColor);
        setCoverImage(updatedLessonData.coverImage);

        if (sortedParticles && sortedParticles.length > 0) {
          const fetchedPairs = await Promise.all(
            sortedParticles.map(async (item: any, index: number) => {
              const particleData = await fetchParticleById(item.type);
              return {
                id: item.id,
                dropdownValue: {
                  id: particleData.id,
                  name: particleData.name,
                  pId: particleData.pId,
                  particleType: particleData.particleType,
                  particleValue: particleData.particleValue,
                  particleType1: particleData.particleType1,
                  particleValue1: particleData.particleValue1,
                  children: particleData.children || [],
                },
                inputValue: item.value,
                order: item.order || index + 1,
                isReadOnly: getParticleReadOnlyStatus(particleData),
              };
            })
          );
          setPairs(fetchedPairs);
        }

        Toast(response?.data.statusMessage, "success");
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleSaveTheme = async () => {
    if (!themeName) {
      setThemeError("Please enter theme name");
      return;
    }

    const activeLessonId = lessonId || Number(id);

    if (!activeLessonId) {
      setThemeError("Lesson ID is missing.");
      return;
    }
    if (editThemes) {
      // If editThemes exists, update the theme
      await handleUpdateTheme(editThemes.id, themeName);
    } else {
      // Otherwise, create a new theme
      await handleCreateTheme(activeLessonId, themeName);
    }
  };

  const handleCreateTheme = async (lessonId: number, themeName: string) => {
    try {
      const nextOrder = themes.length + 1;

      const reqData = {
        lessonId,
        name: themeName,
        order: nextOrder,
      };

      setIsLoading(true);
      const response = await postRequest(
        `${baseURL}/lesson/create-theme`,
        reqData
      );
      setIsLoading(false);
      const newTheme: Theme = {
        id: response?.data.data.id,
        name: response?.data.data.name,
        cards: 0,
        order: response?.data.data.order,
        color: response?.data.data.color || "#FFFFFF",
      };

      setThemes([...themes, newTheme]);
      setThemeName("");
      setNewThemes(false);
    } catch (error) {
      setThemeError("Error creating Theme");
    }
  };

  const handleUpdateTheme = async (themeId: number, themeName: string) => {
    try {
      const reqData = {
        name: themeName,
      };

      setIsLoading(true);
      const response = await postRequest(
        `${baseURL}/lesson/update-themes/${themeId}`,
        reqData
      );
      setIsLoading(false);

      const updatedThemes = themes.map((theme) =>
        theme.id === themeId ? { ...theme, name: themeName } : theme
      );

      setThemes(updatedThemes);
      setThemeName("");
      setNewThemes(false);
      setEditThemes(null);
      Toast(response?.data.statusMessage, "success");
    } catch (error) {
      setThemeError("Error updating Theme");
    }
  };

  const handleDeleteTheme = (id: number, name: string) => {
    setThemeToDelete(id);
    setDeleteDialogMessage(`Are you sure you want to delete this "${name}" ?`);
    setOnConfirmDelete(() => () => handleConfirmDeleteTheme(id));
    setShowDeleteDialog(true);
  };

  /** Duplicate theme in-place immediately — original shortcut behavior */
  const handleDuplicateTheme = async (themeId: number) => {
    try {
      const response = await postRequest(`${baseURL}/lesson/clone-theme`, { id: themeId });
      if (response?.data.success) {
        Toast(response.data.statusMessage || "Theme duplicated successfully", "success");
        const cloned: any = response.data.data;
        const cardCountResponse = await postRequest(`${baseURL}/lesson/counts`, {
          modelName: "subthemes",
          field: "themeId",
          id: cloned.id.toString(),
        });
        const cardsCount = cardCountResponse?.data?.data?.count ?? 0;
        setThemes((prev) => [
          ...prev,
          { id: cloned.id, name: cloned.name, cards: cardsCount, order: cloned.order, color: cloned.color || "#FFFFFF" },
        ]);
      } else {
        Toast("Failed to duplicate theme", "error");
      }
    } catch (error) {
      console.error("Error duplicating theme:", error);
      Toast("Failed to duplicate theme", "error");
    }
  };

  /** Copy theme to global clipboard — no server call yet */
  const handleCopyTheme = (themeId: number) => {
    const theme = themes.find((t) => t.id === themeId);
    if (!theme) return;
    dispatch(
      copyToClipboard({
        items: [{ id: theme.id, name: theme.name }],
        itemType: "theme",
        sourceParentId: lessonId ?? Number(id),
      })
    );
    Toast(`"${theme.name}" copied. Navigate to any lesson and click Paste.`, "success");
  };

  /** Cut theme to global clipboard — no server call yet */
  const handleCutTheme = (themeId: number) => {
    const theme = themes.find((t) => t.id === themeId);
    if (!theme) return;
    dispatch(
      cutToClipboard({
        items: [{ id: theme.id, name: theme.name }],
        itemType: "theme",
        sourceParentId: lessonId ?? Number(id) ?? 0,
      })
    );
    Toast(`"${theme.name}" cut. Navigate to the destination lesson and click Paste.`, "success");
  };

  /** Toggle a single theme in the selection set */
  const toggleThemeSelection = (themeId: number) => {
    setSelectedThemes((prev) =>
      prev.includes(themeId) ? prev.filter((x) => x !== themeId) : [...prev, themeId]
    );
  };

  /** Bulk copy all selected themes to clipboard */
  const handleCopySelectedThemes = () => {
    if (selectedThemes.length === 0) return;
    const items = themes
      .filter((t) => selectedThemes.includes(t.id))
      .map((t) => ({ id: t.id, name: t.name }));
    dispatch(
      copyToClipboard({
        items,
        itemType: "theme",
        sourceParentId: lessonId ?? Number(id),
      })
    );
    Toast(`${items.length} theme(s) copied to clipboard.`, "success");
    setSelectedThemes([]);
  };

  /** Bulk cut all selected themes to clipboard */
  const handleCutSelectedThemes = () => {
    if (selectedThemes.length === 0) return;
    const items = themes
      .filter((t) => selectedThemes.includes(t.id))
      .map((t) => ({ id: t.id, name: t.name }));
    dispatch(
      cutToClipboard({
        items,
        itemType: "theme",
        sourceParentId: lessonId ?? Number(id) ?? 0,
      })
    );
    Toast(`${items.length} theme(s) cut. Navigate to the destination lesson and click Paste.`, "success");
    setSelectedThemes([]);
  };

  /**
   * Paste clipboard themes into THIS lesson.
   * Copy → clones the theme here (original untouched).
   * Cut  → if destination is a DIFFERENT lesson: clone here then delete original.
   *         if destination is the SAME lesson: nothing changes (item stays as-is).
   */
  const handlePasteThemes = async () => {
    if (clipboard.itemType !== "theme" || clipboard.items.length === 0) return;
    const targetLessonId = lessonId ?? Number(id);
    if (!targetLessonId) {
      Toast("Please save the lesson before pasting themes.", "error");
      return;
    }

    // If cut + pasting back into the same lesson → just cancel the cut, nothing moves
    if (clipboard.action === "cut" && clipboard.sourceParentId === targetLessonId) {
      dispatch(clearClipboard());
      Toast("Pasted back into the same lesson — item restored in place.", "success");
      return;
    }

    setIsPasting(true);
    try {
      for (const item of clipboard.items) {
        const response = await postRequest(`${baseURL}/lesson/clone-theme`, {
          id: item.id,
          targetLessonId,
        });
        if (response?.data.success) {
          const cloned: any = response.data.data;
          const cardCountResponse = await postRequest(`${baseURL}/lesson/counts`, {
            modelName: "subthemes",
            field: "themeId",
            id: cloned.id.toString(),
          });
          const cardsCount = cardCountResponse?.data?.data?.count ?? 0;
          setThemes((prev) => [
            ...prev,
            { id: cloned.id, name: cloned.name, cards: cardsCount, order: cloned.order, color: cloned.color || "#FFFFFF" },
          ]);
        } else {
          Toast(`Failed to paste "${item.name}"`, "error");
        }
      }
      if (clipboard.action === "cut") {
        // Delete originals only when moving to a DIFFERENT lesson
        for (const item of clipboard.items) {
          await deleteRequest(`${baseURL}/lesson/delete-themes/${item.id}`);
          // Remove the dimmed original from the current view if we are on the source lesson
          setThemes((prev) => prev.filter((t) => t.id !== item.id));
        }
        Toast(`${clipboard.items.length} theme(s) moved successfully.`, "success");
      } else {
        Toast(`${clipboard.items.length} theme(s) pasted successfully.`, "success");
      }
      dispatch(clearClipboard());
    } catch (error) {
      console.error("Error pasting themes:", error);
      Toast("Failed to paste themes", "error");
    } finally {
      setIsPasting(false);
    }
  };

  const handleConfirmDeleteTheme = async (id: number) => {
    try {
      setLoading(true);
      const response = await deleteRequest(
        `${baseURL}/lesson/delete-themes/${id}`
      );
      setLoading(false);
      const updatedThemes = themes.filter((theme) => theme.id !== id);
      setThemes(updatedThemes);
      setShowDeleteDialog(false);
      setThemeToDelete(null);
      Toast(response?.data.statusMessage, "success");
    } catch (error) {
      console.error("Error deleting flashcard:", error);
      setThemeError("Error deleting flashcard");
      setShowDeleteDialog(false);
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setThemeToDelete(null);
  };

  const handleEditTheme = (theme: Theme) => {
    setThemeName(theme.name);
    setEditThemes(theme);
    setNewThemes(true);
  };

  const handleThemeColorChange = (themeId: number, color: string) => {
    setThemes((prev) =>
      prev.map((t) => (t.id === themeId ? { ...t, color } : t))
    );
  };

  const handleAddSubTheme = (id: number | null, name: string) => {
    // Assuming your sub-themes page route is '/sub-themes'
    navigate(`/lessons/theme/${id}`, {
      state: { themeId: id, themeName: name },
    });
  };

  const handleDropdownChange = (index: number, item: DropdownValue) => {
    const updatedPairs: Pair[] = [...pairs];

    particlePairValues(item, index, updatedPairs);

    const updatedErrors = { ...validationError };
    if (item.particleValue || item.particleValue1) {
      delete updatedErrors.pairs[index];
    } else if (updatedErrors.pairs[index]) {
      updatedErrors.pairs[index].dropdownValue = "";
    } else {
      updatedErrors.pairs[index] = { dropdownValue: "" };
    }
    setPairs(updatedPairs);
    setValidationError(updatedErrors);
  };

  const addNewPair = () => {
    const reorderedPairs = pairs
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((pair, index) => ({
        ...pair,
        order: index + 1,
      }));

    const newPair = {
      dropdownValue: null,
      inputValue: "",
      order: reorderedPairs.length + 1,
    };
    setPairs([...reorderedPairs, newPair]);
  };
  const removePair = (index: number) => {
    const updatedPairs = [...pairs];
    updatedPairs.splice(index, 1);
    setPairs(updatedPairs);
  };

  const handleDeletePair = (index: number, id?: number) => {
    if (id !== undefined) {
      setPairToDelete({ index, id });
      setDeleteDialogMessage("Are you sure you want to delete this pair?");
      setOnConfirmDelete(() => () => handleConfirmDeletePair(index, id)); // Pass index and ID to confirm function
      setShowDeleteDialog(true);
    } else {
      removePair(index);
    }
  };
  const handleConfirmDeletePair = async (index: number, id?: number) => {
    if (id !== undefined) {
      try {
        const reqData = {
          modelName: "lessonIntros",
          id: id,
        };

        const response = await postRequest(
          `${baseURL}/lesson/delete-bretzel-content`,
          reqData
        );
        if (response?.data) {
          const updatedPairs = [...pairs];
          updatedPairs.splice(index, 1);
          setPairs(updatedPairs);
          Toast(response?.data.statusMessage, "success");
        } else {
          console.error("Error deleting pair:", response?.data);
        }
      } catch (error) {
        console.error("Error deleting pair:", error);
      }
    } else {
      const updatedPairs = [...pairs];
      updatedPairs.splice(index, 1);
      setPairs(updatedPairs);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    const updatedPairs = [...pairs];
    const pair = updatedPairs[index];

    // Handle particleValue (black type) - should be at the beginning and fixed
    if (pair.dropdownValue?.particleType === "black" && pair.dropdownValue?.particleValue) {
      value = ensureParticleValueAtStart(
        value,
        pair.dropdownValue.particleValue
      );
    }

    // Handle particleValue1 (other types) - should be at the end
    // BUT skip if particleValue1 is "..." (placeholder for user input)
    if (pair.dropdownValue?.particleValue1 && pair.dropdownValue.particleValue1 !== "...") {
      value = ensureParticleValue1AtEnd(
        value,
        pair.dropdownValue.particleValue1
      );
    }

    updatedPairs[index].inputValue = value;

    const updatedErrors = { ...validationError };
    if (updatedErrors.pairs[index]) {
      updatedErrors.pairs[index].inputValue = "";
    } else {
      updatedErrors.pairs[index] = { inputValue: "" };
    }

    setPairs(updatedPairs);
    setValidationError(updatedErrors);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const pair = pairs[index];
    const input = e.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;

    if (pair.dropdownValue?.particleType === "black" && pair.dropdownValue?.particleValue) {
      const fixedValue = pair.dropdownValue.particleValue.trim();
      const fixedLength = fixedValue.length;

      // Prevent deletion of the fixed particle value at the beginning
      if (
        (e.key === 'Backspace' && cursorPosition <= fixedLength) ||
        (e.key === 'Delete' && cursorPosition < fixedLength) ||
        (e.key === 'ArrowLeft' && cursorPosition <= fixedLength && (e.ctrlKey || e.metaKey)) ||
        (cursorPosition < fixedLength && e.key.length === 1) // Prevent typing within the fixed part
      ) {
        e.preventDefault();
        // Move cursor to the end of fixed value
        setTimeout(() => {
          input.setSelectionRange(fixedLength, fixedLength);
        }, 0);
      }
    }
  };


  const handleBackClick = () => {
    const params = new URLSearchParams({ page: listPage.toString(), limit: listLimit.toString() });
    if (listSearch) params.set("search", listSearch);
    if (listFilter && listFilter !== "All") params.set("filter", listFilter);
    if (listLevel && listLevel !== "All Levels") params.set("level", listLevel);
    navigate(`/lessons?${params.toString()}`);
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.2,
      ease: "power2.out",
    });

    timeLine.from(".left-container", {
      opacity: 0,
      x: -40,
      duration: 0.3,
      ease: "power2.out",
      stagger: 0.2,
    });

    timeLine.from(".right-container", {
      opacity: 0,
      x: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2,
    });
  }, [isLoading]);

  return (
    <div className="container-fluid">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="p-3 main-page">
            <div
              className="d-flex align-items-center go-back"
              onClick={handleBackClick}
              style={{ width: "fit-content" }}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                className="me-2"
                style={{
                  backgroundColor: "#D9D9D9",
                  padding: "7px",
                  borderRadius: "8px",
                  color: "#000",
                  cursor: "pointer",
                }}
              />
              <label
                style={{
                  color: "#FF9E00",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Back
              </label>
            </div>
            <Label
              fontSize="22px"
              alignItems="center"
              display="flex"
              justifyContent="center"
              color="#7542E4"
              fontWeight={700}
              className="row-container"
            >
              {id || lessonId ? "Edit" : "Add"} a lesson
            </Label>
            <VerticalSpace height={20} />
            <div className="row p-3 d-flex main-container justify-content-between">
              <div className="col lesson-container left-container">
                <div
                  className="container p-4"
                  style={{
                    backgroundColor: "rgb(220 220 220 / 66%)",
                    borderRadius: "30px",
                  }}
                >
                  <div className="d-flex justify-content-between lessons-top align-items-center">
                    <RowContainer gap="0px">
                      <button
                        type="button"
                        className={`btn ${selectedButton === "Cover"
                          ? "btn-success"
                          : "btn-outline-dark"
                          } me-2 text-center user`}
                        style={{
                          color:
                            selectedButton === "Cover" ? "#FFFFFF" : "#100F15",
                          backgroundColor:
                            selectedButton === "Cover"
                              ? "#000000"
                              : "transparent",
                          border:
                            selectedButton === "Cover"
                              ? "none"
                              : "1px solid #100F15",
                          borderRadius: "46px",
                          padding: "8px 30px",
                          fontSize: "12px",
                          width: "auto",
                        }}
                        onClick={() => handleButtonClick("Cover")}
                      >
                        Cover
                      </button>
                    </RowContainer>
                    <div className="d-flex" style={{ gap: "10px" }}>
                      <RadioButton
                        checked={level === "A1"}
                        onChange={() => handleLevelChange("A1")}
                      />
                      <Label fontSize="14px" fontWeight={400}>
                        A1
                      </Label>
                      <RadioButton
                        checked={level === "A2"}
                        onChange={() => handleLevelChange("A2")}
                      />
                      <Label fontSize="14px" fontWeight={400}>
                        A2
                      </Label>

                      <RadioButton
                        checked={level === "B1"}
                        onChange={() => handleLevelChange("B1")}
                      />
                      <Label fontSize="14px" fontWeight={400}>
                        B1
                      </Label>

                      <RadioButton
                        checked={level === "B2"}
                        onChange={() => handleLevelChange("B2")}
                      />
                      <Label fontSize="14px" fontWeight={400}>
                        B2
                      </Label>
                    </div>
                  </div>
                  <VerticalSpace height={10} />
                  {showCoverForm && (
                    <div className="col">
                      <div className="col-12">
                        <div className="d-flex justify-content-between">
                          <Label fontSize="20px" fontWeight={700}>
                            Title
                          </Label>
                        </div>
                        <VerticalSpace height={10} />
                        <input
                          type="text"
                          placeholder="Tiempos Verbales"
                          className={`form-control ${errors.title ? "is-invalid" : ""
                            }`}
                          id="lessonstitle"
                          style={{
                            borderRadius: "10px",
                            height: "40px",
                            fontSize: "14px",
                          }}
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                        />
                        {errors.title && (
                          <div className="invalid-feedback">{errors.title}</div>
                        )}
                      </div>
                      <VerticalSpace height={20} />
                      {/* <RowContainer
                        className="color-set"
                        alignItem="center"
                        justifyContent="space-between"
                      >
                        <ColumnContainer
                          alignItem="center"
                          className="item-color"
                        >
                          <Label
                            fontSize="14px"
                            width="max-content"
                            fontWeight={400}
                          >
                            Title color
                          </Label>
                          <input
                            type="color"
                            value={titleColor}
                            className="form-control form-control-color user"
                            id="myColor"
                            onChange={(e) => setTitleColor(e.target.value)}
                          />
                        </ColumnContainer>
                        <ColumnContainer
                          alignItem="center"
                          className="item-color"
                        >
                          <Label
                            fontSize="14px"
                            width="max-content"
                            fontWeight={400}
                          >
                            Theme text color
                          </Label>
                          <input
                            type="color"
                            value={subTitleColor}
                            className="form-control form-control-color user"
                            id="myColor"
                            onChange={(e) => setSubTitleColor(e.target.value)}
                          />
                        </ColumnContainer>
                        <ColumnContainer
                          alignItem="center"
                          className="item-color"
                        >
                          <Label
                            fontSize="14px"
                            width="max-content"
                            fontWeight={400}
                          >
                            Theme bg-color
                          </Label>
                          <input
                            type="color"
                            value={subTitleBgColor}
                            className="form-control form-control-color user"
                            id="myColor"
                            onChange={(e) => setSubTitleBgColor(e.target.value)}
                          />
                        </ColumnContainer>
                      </RowContainer> */}
                      {/* <VerticalSpace height={20} />
                      {!coverImage && (
                        <div className="col-12">
                          <Label fontSize="20px" fontWeight={700}>
                            Cover Image
                          </Label>
                          <VerticalSpace height={4} />
                          <div className="drop_box">
                            {coverImageLoading ? (
                              <RowContainer
                                alignItem="center"
                                justifyContent="center"
                                padding="30px"
                              >
                                <Loader />
                              </RowContainer>
                            ) : (
                              <>
                                <header>
                                  <h4 className="image-text">
                                    Upload a&nbsp;
                                    <span style={{ color: "#7542E4" }}>
                                      cover
                                    </span>
                                    &nbsp;image for the lesson section
                                  </h4>
                                  <h4>jpg, png or svg</h4>
                                </header>

                                <input
                                  type="file"
                                  className={`form-control ${errors.coverImage ? "is-invalid" : ""
                                    }`}
                                  ref={fileInputRef}
                                  hidden
                                  accept="image/*"
                                  onChange={handleCoverImageChange}
                                />

                                <VerticalSpace height={10} />
                                <button
                                  type="button"
                                  className="user"
                                  style={{
                                    color: "#100F15",
                                    background: "none",
                                    border: "1px solid #100F15",
                                    borderRadius: "10px",
                                    padding: "14px 30px",
                                    fontSize: "14px",
                                    width: "auto",
                                  }}
                                  onClick={() => handleChooseFileClick("cover")}
                                >
                                  Choose file
                                </button>
                                {errors.coverImage && (
                                  <div className="invalid-feedback d-flex justify-content-center">
                                    {errors.coverImage}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {coverImage && (
                        <div className="col-12">
                          <Label fontSize="20px" fontWeight={700}>
                            Cover Image
                          </Label>
                          <VerticalSpace height={4} />
                          <div
                            className="drop_box"
                            style={{
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            {coverImageLoading ? (
                              <RowContainer
                                alignItem="center"
                                justifyContent="center"
                                padding="30px"
                              >
                                <Loader />
                              </RowContainer>
                            ) : (
                              <>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                >
                                  {typeof coverImage === "string" ? (
                                    <>
                                      <img
                                        src={coverImage}
                                        alt="Cover"
                                        style={{
                                          maxWidth: "100px",
                                          maxHeight: "100px",
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          handleChooseFileClick("cover")
                                        }
                                      />
                                      <input
                                        type="file"
                                        ref={fileInputRef} // Attach ref to file input
                                        hidden
                                        accept="image/*"
                                        onChange={handleCoverImageChange}
                                      />
                                    </>
                                  ) : (
                                    coverImage && (
                                      <img
                                        src={URL.createObjectURL(coverImage)}
                                        alt="Cover"
                                        style={{
                                          maxWidth: "100px",
                                          maxHeight: "100px",
                                        }}
                                      />
                                    )
                                  )}
                                </div>
                                <div
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    handleDeleteImage("cover");
                                  }}
                                >
                                  <img
                                    src={deleteIcon}
                                    className="user-scale"
                                    alt="Delete"
                                    style={{
                                      WebkitUserSelect: "none",
                                      userSelect: "none",
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )} */}
                      <VerticalSpace height={20} />
                      <div className="col-12">
                        <Label fontSize="20px" fontWeight={700}>
                          Partículas
                        </Label>
                        <VerticalSpace height={10} />
                        <div
                          className="col d-flex flex-column align-itmes-center particle-example"
                          style={{ padding: "0px 14px 0px 0px", gap: "16px" }}
                        >
                          {pairs.map((pair, index) => (
                            <div
                              key={index}
                              className="row particle-row"
                              style={{
                                flexWrap: "unset",
                                alignItems: "center",
                              }}
                            >
                              <div
                                className="col particle-dropdown"
                                style={{ flex: "1", minWidth: "0" }}
                              >
                                <NestedMenu
                                  data={
                                    pair.dropdownValue ? [pair.dropdownValue!] : []
                                  }
                                  onSelectItem={(value) =>
                                    handleDropdownChange(
                                      index,
                                      value as DropdownValue
                                    )
                                  }
                                  borderColor={
                                    validationError.pairs[index]?.dropdownValue
                                      ? "#dc3545"
                                      : "#D9D9D9"
                                  }
                                />
                                <div
                                  className="p-0 delete-icon"
                                  style={{
                                    cursor: "pointer",
                                    width: "fit-content",
                                  }}
                                  onClick={() => handleDeletePair(index, pair.id)}
                                >
                                  <img
                                    src={deleteIcon}
                                    className="user-scale"
                                    style={{
                                      WebkitUserSelect: "none",
                                      userSelect: "none",
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col particle-input">
                                <input
                                  type="text"
                                  value={pair.inputValue || ""}
                                  onChange={(e) =>
                                    handleInputChange(index, e.target.value)
                                  }
                                  onKeyDown={(e) => handleKeyDown(index, e)}
                                  className={`form-control ${validationError.pairs[index]?.inputValue
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                  placeholder=""
                                  style={{
                                    borderRadius: "10px",
                                    height: "40px",
                                    fontSize: "14px",
                                    backgroundColor: pair.isReadOnly
                                      ? "#ffffff00"
                                      : "",
                                    border: pair.isReadOnly
                                      ? "1px solid #D9D9D9"
                                      : "",
                                  }}
                                  readOnly={pair.isReadOnly}
                                />
                              </div>
                              <div
                                className="p-0 delete-button"
                                style={{
                                  cursor: "pointer",
                                  width: "fit-content",
                                }}
                                onClick={() => handleDeletePair(index, pair.id)}
                              >
                                <img src={deleteIcon} className="user-scale" />
                              </div>
                            </div>
                          ))}
                        </div>
                        <VerticalSpace height={16} />

                        <button
                          type="button"
                          className="btn btn-outline-secondary me-2 text-center user"
                          style={{
                            color: "#100F15",
                            background: "none",
                            border: "1px solid #100F15",
                            borderRadius: "10px",
                            padding: "14px 40px",
                            fontSize: "14px",
                            width: "auto",
                          }}
                          onClick={addNewPair}
                        >
                          Add new
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <HorizontalSpace width={5} />
              <div className="col theme-container right-container">
                <div
                  className="container p-4"
                  style={{
                    backgroundColor: "rgb(220 220 220 / 66%)",
                    borderRadius: "30px",
                  }}
                >
                  <div className="col-12 theme-content">
                    <div className="d-flex justify-content-between align-items-center">
                      <Label fontSize="20px" fontWeight={700}>
                        Themes
                      </Label>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {/* Bulk Copy / Cut toolbar — visible only when themes are selected */}
                        {selectedThemes.length > 0 && (
                          <>
                            <button
                              type="button"
                              className="btn user"
                              title={`Copy ${selectedThemes.length} selected theme(s)`}
                              style={{
                                width: "100px",
                                backgroundColor: "#DAD7FE",
                                color: "#000",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 5,
                              }}
                              onClick={handleCopySelectedThemes}
                            >
                              <img
                                src={clipboardIcon}
                                alt="copy"
                                style={{ width: 20, height: 20, }}
                              />
                              Copy ({selectedThemes.length})
                            </button>
                            <button
                              type="button"
                              className="btn user"
                              title={`Cut ${selectedThemes.length} selected theme(s)`}
                              style={{
                                width: "100px",
                                backgroundColor: "#DAD7FE",
                                color: "#000",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 5,
                              }}
                              onClick={handleCutSelectedThemes}
                            >
                              <img
                                src={cutIcon}
                                alt="cut"
                                style={{ width: 20, height: 20, }}
                              />
                              Cut ({selectedThemes.length})
                            </button>
                            <button
                              type="button"
                              className="btn user"
                              title="Clear selection"
                              style={{
                                width: "100px",
                                background: "none",
                                border: "1px solid #aaa",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 10px",
                                color: "#555",
                              }}
                              onClick={() => setSelectedThemes([])}
                            >
                              ✕ Clear
                            </button>
                          </>
                        )}
                        {/* <div
                          style={{
                            backgroundColor: "#FFFFFF",
                            padding: "10px 10px",
                            borderRadius: "10px",
                            border: "1px solid #D9D9D9",
                            alignItems: "center",
                            cursor: "pointer",
                            width: "min-content",
                          }}
                        >
                          <input
                            type="color"
                            value={subTitleBgColor}
                            className="form-control form-control-color border-1 p-0"
                            style={{
                              width: "45px",
                              height: "30px",
                              border: "1px solid #D9D9D9",
                              borderRadius: "10px",
                            }}
                            id="myColor"
                            onChange={(e) => setSubTitleBgColor(e.target.value)}
                          />
                        </div> */}
                      </div>
                    </div>

                    {/* Clipboard paste banner — only visible when theme items are in clipboard */}
                    {clipboard.itemType === "theme" && clipboard.items.length > 0 && (
                      <div
                        style={{
                          background: clipboard.action === "cut" ? "#fff3cd" : "#e8f4fd",
                          border: `1px solid ${clipboard.action === "cut" ? "#ffc107" : "#7542E4"}`,
                          borderRadius: 8,
                          padding: "8px 12px",
                          marginTop: 10,
                          marginBottom: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>
                            <img src={pasteIcon} alt="" />
                            {clipboard.action === "cut" ? "Cut" : "Copied"}:&nbsp;
                          </span>
                          <span style={{ fontSize: 12, color: "#555" }}>
                            {clipboard.items.map((i) => i.name).join(", ")}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            type="button"
                            className="btn user"
                            disabled={isPasting}
                            style={{
                              backgroundColor: "#7542E4",
                              color: "#fff",
                              borderRadius: 8,
                              fontSize: 12,
                              padding: "5px 14px",
                            }}
                            onClick={handlePasteThemes}
                          >
                            {isPasting
                              ? "Pasting…"
                              : `Paste ${clipboard.action === "cut" ? "(Move)" : "(Copy)"} here`}
                          </button>
                          <button
                            type="button"
                            className="btn user"
                            style={{
                              background: "none",
                              border: "1px solid #aaa",
                              borderRadius: 8,
                              fontSize: 12,
                              padding: "5px 10px",
                              color: "#555",
                            }}
                            onClick={() => dispatch(clearClipboard())}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      {themes.length === 0 ? (
                        <div className="p-5 d-flex justify-content-center align-items-center ">
                          <label
                            htmlFor="theme"
                            className="form-label"
                            style={{ fontWeight: "bold", color: "#7542E4" }}
                          >
                            There are no themes yet in this lesson.
                          </label>
                        </div>
                      ) : (
                        themes.map((theme) => (
                          <div
                            className="row d-flex justify-content-center align-items-center"
                            style={{
                              gap: "10px",
                              opacity:
                                clipboard.action === "cut" &&
                                  clipboard.itemType === "theme" &&
                                  clipboard.items.some((ci) => ci.id === theme.id)
                                  ? 0.4
                                  : 1,
                            }}
                            key={theme.id}
                          >
                            {/* Checkbox for bulk selection */}
                            <div
                              className="p-0"
                              style={{ width: "fit-content", cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); toggleThemeSelection(theme.id); }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedThemes.includes(theme.id)}
                                onChange={() => toggleThemeSelection(theme.id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: 16,
                                  height: 16,
                                  accentColor: "#7542E4",
                                  cursor: "pointer",
                                }}
                              />
                            </div>
                            {/* Per-theme color picker */}
                            <div
                              className="p-0"
                              style={{ width: "fit-content" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  padding: "8px 8px",
                                  borderRadius: "10px",
                                  border: "1px solid #D9D9D9",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  width: "min-content",
                                }}
                              >
                                <input
                                  type="color"
                                  value={theme.color || "#FFFFFF"}
                                  className="form-control form-control-color border-1 p-0"
                                  style={{
                                    width: "35px",
                                    height: "30px",
                                    border: "1px solid #D9D9D9",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    outline: "none",
                                    boxShadow: "none",
                                  }}
                                  onChange={(e) => handleThemeColorChange(theme.id, e.target.value)}
                                />
                              </div>
                            </div>
                            <div
                              className="drop_box theme-box col-7 user"
                              style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                outline: selectedThemes.includes(theme.id)
                                  ? "2px solid #7542E4"
                                  : "none",
                              }}
                              onClick={() => handleEditTheme(theme)}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                <Label
                                  fontSize="14px"
                                  color="#000"
                                  fontWeight={400}
                                >
                                  {theme.name}
                                </Label>
                              </div>

                              <div style={{ cursor: "pointer" }}>
                                <Label
                                  fontSize="16px"
                                  color="#000"
                                  fontWeight={400}
                                  cursor="pointer"
                                  padding="8px 13px 8px 13px"
                                  bgColor="#DAD7FE"
                                  justifyContent="center"
                                  display="flex"
                                  borderRadius="40px"
                                  className="user"
                                >
                                  {theme.cards} cards
                                </Label>
                              </div>
                            </div>
                            {/* Duplicate in-place */}
                            <div
                              className="p-0"
                              title="Duplicate here"
                              style={{ cursor: "pointer", width: "fit-content" }}
                              onClick={() => handleDuplicateTheme(theme.id)}
                            >
                              <img
                                src={copyIcon}
                                className="user-scale"
                                alt="Duplicate"
                                style={{ WebkitUserSelect: "none", userSelect: "none" }}
                              />
                            </div>
                            {/* Copy to clipboard */}
                            <div
                              className="p-0"
                              title="Copy to clipboard"
                              style={{ cursor: "pointer", width: "fit-content" }}
                              onClick={() => handleCopyTheme(theme.id)}
                            >
                              <img
                                src={clipboardIcon}
                                className="user-scale"
                                alt="Copy"
                                style={{
                                  WebkitUserSelect: "none",
                                  userSelect: "none",
                                }}
                              />
                            </div>
                            {/* Cut to clipboard */}
                            <div
                              className="p-0"
                              title="Cut to clipboard"
                              style={{ cursor: "pointer", width: "fit-content" }}
                              onClick={() => handleCutTheme(theme.id)}
                            >
                              <img
                                src={cutIcon}
                                className="user-scale"
                                alt="Cut"
                                style={{ WebkitUserSelect: "none", userSelect: "none" }}
                              />
                            </div>
                            <div
                              className="p-0"
                              style={{
                                cursor: "pointer",
                                width: "fit-content",
                              }}
                              onClick={() =>
                                handleAddSubTheme(theme.id, theme.name)
                              }
                            >
                              <img
                                src={editIcon}
                                className="user-scale"
                                style={{
                                  WebkitUserSelect: "none",
                                  userSelect: "none",
                                }}
                              />
                            </div>
                            <div
                              className="p-0"
                              style={{
                                cursor: "pointer",
                                width: "fit-content",
                              }}
                              onClick={() =>
                                handleDeleteTheme(theme.id, theme.name)
                              }
                            >
                              <img
                                src={deleteIcon}
                                style={{
                                  WebkitUserSelect: "none",
                                  userSelect: "none",
                                }}
                                className="user-scale"
                              />
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <button
                      type="button"
                      className="user"
                      style={{
                        color: "#100F15",
                        background: "none",
                        border: "1px solid #100F15",
                        borderRadius: "10px",
                        padding: "14px 40px",
                        fontSize: "14px",
                        width: "auto",
                      }}
                      onClick={handleNewThemes}
                    >
                      Add new
                    </button>
                  </div>
                </div>
              </div>
              <VerticalSpace height={200} />
              <div
                className="container row d-flex justify-content-center publish-button"
                style={{
                  position: "fixed",
                  height: "12%",
                  bottom: "0px",
                  zIndex: 1,
                  background:
                    "linear-gradient(178.35deg, rgba(255, 255, 255, 0) 1.4%, rgba(255, 255, 255, 0.69) 26.79%, #FFFFFF 50.22%)",
                }}
              >
                <div
                  className="d-flex justify-content-center"
                  style={{
                    position: "fixed",
                    zIndex: 1,
                    bottom: "34px",

                    backgroundColor: "#FFFFFF",
                  }}
                >
                  {" "}
                  <button
                    type="button"
                    className="btn btn-success d-flex justify-content-center user"
                    style={{
                      backgroundColor: "#FF9E00",
                      color: "#100F15",
                      borderRadius: "10px",
                      height: "fit-content",
                      padding: "10px 20%",
                      width: "400px",
                      fontSize: "14px",
                    }}
                    onClick={handlePublishClick}
                  >
                    Publish
                  </button>
                </div>
              </div>
            </div>
          </div>

          {addThemes && (
            <div
              className="modal"
              role="dialog"
              style={{
                display: "block",
                position: "fixed",
                zIndex: 3,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              }}
            >
              <div className="modal-dialog">
                <div className="modal-content" style={{ borderRadius: "20px" }}>
                  <div className="modal-body">
                    <h5 style={{ fontWeight: "bold" }}>Add a theme</h5>
                    <VerticalSpace height={10} />
                    <input
                      type="text"
                      className="form-control"
                      id="inputthemes"
                      placeholder="New theme name"
                      value={themeName}
                      onChange={(e) => {
                        setThemeName(e.target.value);
                        if (themeError && e.target.value) {
                          setThemeError("");
                        }
                      }}
                    />
                    {themeError && (
                      <div className="error-message">{themeError}</div>
                    )}
                  </div>
                  <div className="modal-body d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-secondary user"
                      onClick={() => setNewThemes(false)}
                      style={{
                        // height: "50px",
                        padding: "12px",
                        color: "#100F15",
                        background: "none",
                        borderRadius: "10px",
                        border: "1px solid #100F15",
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger user"
                      style={{
                        backgroundColor: "#FF9E00",
                        color: "#100F15",
                        borderRadius: "10px",
                      }}
                      onClick={handleSaveTheme}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )
      }
      {
        showDeleteDialog && (
          <DeleteConfirmation
            showDeletePopup={true}
            setShowDeletePopup={setShowDeleteDialog}
            message={deleteDialogMessage}
            deleteButtonLabel="Delete"
            onDelete={onConfirmDelete}
            onCancel={handleCancelDelete}
          />
        )
      }

      {loading && <DeleteLoader />}
    </div >
  );
};

export default CreateLessons;
